/* Prayer times + the day's notification plan.

   SHARED between the app (src/) and the push worker (push-worker/). This is the
   one deliberate departure from commanddeck, where index.html and the worker
   each carried a hand-mirrored copy that had to "reach identical conclusions".
   Here there is one implementation, so they can't disagree. Keep it free of
   DOM and Worker-only APIs. */
import { fastingInfo } from './fasting';
import {
  CalculationMethod, CalculationParameters, Coordinates, HighLatitudeRule, Madhab, PrayerTimes, Qibla, SunnahTimes,
} from 'adhan';

export const PRAYERS = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'] as const;
export type PrayerKey = (typeof PRAYERS)[number];
export const SALAH: Exclude<PrayerKey, 'sunrise'>[] = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];

export const PRAYER_LABEL: Record<PrayerKey, { en: string; ar: string }> = {
  fajr: { en: 'Fajr', ar: 'الفجر' },
  sunrise: { en: 'Sunrise', ar: 'الشروق' },
  dhuhr: { en: 'Dhuhr', ar: 'الظهر' },
  asr: { en: 'Asr', ar: 'العصر' },
  maghrib: { en: 'Maghrib', ar: 'المغرب' },
  isha: { en: 'Isha', ar: 'العشاء' },
};

export const METHODS = {
  MuslimWorldLeague: 'Muslim World League',
  Egyptian: 'Egyptian General Authority',
  Karachi: 'University of Islamic Sciences, Karachi',
  UmmAlQura: 'Umm al-Qura, Makkah',
  Dubai: 'Dubai',
  MoonsightingCommittee: 'Moonsighting Committee',
  NorthAmerica: 'ISNA (North America)',
  Kuwait: 'Kuwait',
  Qatar: 'Qatar',
  Singapore: 'Singapore / Malaysia',
  Tehran: 'Tehran',
  Turkey: 'Diyanet (Turkey)',
} as const;
export type MethodKey = keyof typeof METHODS;

export interface PrayerSettings {
  lat: number;
  lng: number;
  tz: string;            // IANA zone of the location, e.g. "Europe/London"
  placeName?: string;
  method: MethodKey;
  madhab: 'shafi' | 'hanafi';
  highLat: 'auto' | 'middleofthenight' | 'seventhofthenight' | 'twilightangle';
  adjust: Partial<Record<PrayerKey, number>>; // minutes, clamped ±60
}

export const DEFAULT_PRAYER_SETTINGS: PrayerSettings = {
  lat: 51.5074, lng: -0.1278, tz: 'Europe/London', placeName: 'London',
  method: 'MoonsightingCommittee', madhab: 'shafi', highLat: 'auto', adjust: {},
};

const clampAdj = (v: unknown) => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.max(-60, Math.min(60, n)) : 0;
};

function params(s: PrayerSettings, coords: Coordinates): CalculationParameters {
  const factory = (CalculationMethod as unknown as Record<string, () => CalculationParameters>)[s.method]
    ?? CalculationMethod.MuslimWorldLeague;
  const p = factory();
  p.madhab = s.madhab === 'hanafi' ? Madhab.Hanafi : Madhab.Shafi;
  p.highLatitudeRule = s.highLat === 'auto'
    ? HighLatitudeRule.recommended(coords)
    : ({ middleofthenight: HighLatitudeRule.MiddleOfTheNight, seventhofthenight: HighLatitudeRule.SeventhOfTheNight, twilightangle: HighLatitudeRule.TwilightAngle } as const)[s.highLat];
  for (const k of PRAYERS) (p.adjustments as Record<string, number>)[k] = clampAdj(s.adjust?.[k]);
  return p;
}

/* adhan computes for a calendar date; which date is "today" depends on the
   location's zone, not the device's (the worker runs in UTC). */
export function dayKeyIn(tz: string, at = new Date()): string {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(at).map((x) => [x.type, x.value]),
  );
  return `${p.year}-${p.month}-${p.day}`;
}

export function addDays(dayKey: string, n: number): string {
  const [y, m, d] = dayKey.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}

export type DayTimes = Record<PrayerKey, Date> & { dayKey: string; midnight: Date; lastThird: Date };

/* Pure in (settings, day), and asked for many times: the timer every second, and each plan
   for neighbouring days. A small cache keeps the push worker inside its CPU budget. */
const MEMO_MAX = 64;
function memo<T>(cache: Map<string, T>, s: PrayerSettings, dayKey: string, make: () => T): T {
  const k = `${dayKey}|${s.lat}|${s.lng}|${s.method}|${s.madhab}|${s.highLat}|${JSON.stringify(s.adjust ?? {})}`;
  let v = cache.get(k);
  if (v === undefined) {
    v = make();
    if (cache.size >= MEMO_MAX) cache.delete(cache.keys().next().value!);
    cache.set(k, v);
  }
  return v;
}
const timesCache = new Map<string, DayTimes>();
const marksCache = new Map<string, { asrDoubleShadow: Date | null; redTwilightEnd: Date | null }>();

export function timesFor(s: PrayerSettings, dayKey: string): DayTimes {
  return memo(timesCache, s, dayKey, () => computeTimes(s, dayKey));
}
function computeTimes(s: PrayerSettings, dayKey: string): DayTimes {
  const coords = new Coordinates(s.lat, s.lng);
  const [y, m, d] = dayKey.split('-').map(Number);
  // adhan reads the local Y/M/D of the Date it's given — build it locally.
  const pt = new PrayerTimes(coords, new Date(y, m - 1, d), params(s, coords));
  const sun = new SunnahTimes(pt);
  return {
    dayKey,
    fajr: pt.fajr, sunrise: pt.sunrise, dhuhr: pt.dhuhr, asr: pt.asr, maghrib: pt.maghrib, isha: pt.isha,
    midnight: sun.middleOfTheNight, lastThird: sun.lastThirdOfTheNight,
  };
}

/* Two boundaries inside a prayer's window that the five times don't give (used by the
   prayer timer, src/features/prayer/window.ts):
     asrDoubleShadow  when a shadow is twice its length — the Ḥanafī start of ʿAsr, and where the
                      Shāfiʿī / Ḥanbalī *preferred* time for ʿAsr ends.
     redTwilightEnd   when the red glow after sunset has gone (the sun about 12° below the horizon),
                      where the majority say Maghrib ends. Worked out by sun angle, whatever
                      angle or fixed interval the chosen method uses for ʿIshāʾ.
   Either is null if it can't be worked out (extreme latitudes). */
export function windowMarks(s: PrayerSettings, dayKey: string): { asrDoubleShadow: Date | null; redTwilightEnd: Date | null } {
  return memo(marksCache, s, dayKey, () => computeMarks(s, dayKey));
}
function computeMarks(s: PrayerSettings, dayKey: string): { asrDoubleShadow: Date | null; redTwilightEnd: Date | null } {
  const coords = new Coordinates(s.lat, s.lng);
  const [y, m, d] = dayKey.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const at = (make: (p: CalculationParameters) => Date) => {
    try { const t = make(params(s, coords)); return Number.isFinite(t.getTime()) ? t : null; } catch { return null; }
  };
  return {
    asrDoubleShadow: at((p) => { p.madhab = Madhab.Hanafi; return new PrayerTimes(coords, date, p).asr; }),
    redTwilightEnd: at((p) => {
      // A plain sun-angle ʿIshāʾ: not the method's own (Moonsighting's seasonal rule, a fixed interval, …) and no personal adjustment.
      p.method = 'Other'; p.ishaInterval = 0; p.ishaAngle = 12; p.adjustments.isha = 0; p.methodAdjustments = { ...p.methodAdjustments, isha: 0 };
      return new PrayerTimes(coords, date, p).isha;
    }),
  };
}

const MIN = 60_000;
export type Fard = Exclude<PrayerKey, 'sunrise'>;
export interface PhaseNight { maghrib: number; nextFajr: number }
type Marks = ReturnType<typeof windowMarks>;

/** Amber after half the window, red for the last fifth (at least 15 minutes). */
function evenPhases(start: number, end: number) {
  const len = end - start;
  return { lateAt: start + len * 0.5, endingAt: end - Math.max(15 * MIN, len * 0.2) };
}
const within = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Where a prayer's preferred time (green) ends and its disliked / ending stretch (red) begins.
    Always start ≤ lateAt ≤ endingAt ≤ end. The reasoning per prayer is at the top of
    src/features/prayer/window.ts. Shared so the timer and the "running late" push agree. */
export function prayerPhases(key: Fard, s: PrayerSettings, dayKey: string, start: number, end: number, night: PhaseNight, marks: Marks = windowMarks(s, dayKey)) {
  const len = end - start;
  let p = evenPhases(start, end);
  if (key === 'fajr') {
    p = { lateAt: start + len * 0.5, endingAt: end - 20 * MIN };
  } else if (key === 'asr') {
    // red from the sun yellowing (about 20 minutes before sunset, more in a long afternoon)
    const yellow = Math.min(p.endingAt, end - 20 * MIN);
    const double = marks.asrDoubleShadow;
    // Ḥanafī: ʿAsr starts at double shadow already, so everything before the yellowing is the preferred time.
    p = { lateAt: s.madhab === 'hanafi' ? yellow : double ? double.getTime() : p.lateAt, endingAt: yellow };
  } else if (key === 'maghrib') {
    const redGone = marks.redTwilightEnd;
    const lateAt = start + Math.min(20 * MIN, len * 0.5);
    p = { lateAt, endingAt: redGone ? redGone.getTime() : start + len * 0.75 };
    p.endingAt = Math.min(p.endingAt, end - 10 * MIN); // always warn for the last 10 minutes
  } else if (key === 'isha') {
    p = { lateAt: night.maghrib + (night.nextFajr - night.maghrib) / 3, endingAt: end }; // a third of the night; red only once past the middle
  }
  const lateAt = within(p.lateAt, start, end);
  return { lateAt, endingAt: within(p.endingAt, lateAt, end) };
}

/** Each prayer's window on a day (ʿIshāʾ to the middle of the night), with its phases. */
export function dayWindows(s: PrayerSettings, t: DayTimes): Record<Fard, { start: number; end: number; lateAt: number; endingAt: number }> {
  const marks = windowMarks(s, t.dayKey);
  // The middle of the night is Maghrib + half the night, so the next Fajr follows without another day's calculation.
  const night = { maghrib: t.maghrib.getTime(), nextFajr: t.maghrib.getTime() + 2 * (t.midnight.getTime() - t.maghrib.getTime()) };
  const span: Record<Fard, [Date, Date]> = { fajr: [t.fajr, t.sunrise], dhuhr: [t.dhuhr, t.asr], asr: [t.asr, t.maghrib], maghrib: [t.maghrib, t.isha], isha: [t.isha, t.midnight] };
  const out = {} as ReturnType<typeof dayWindows>;
  for (const k of SALAH) {
    const [a, b] = span[k];
    out[k] = { start: a.getTime(), end: b.getTime(), ...prayerPhases(k, s, t.dayKey, a.getTime(), b.getTime(), night, marks) };
  }
  return out;
}

/** "Running late": once this much of a prayer's preferred (green) time has passed and it isn't logged. */
export const LATE_REMINDER_AT = 0.6;

export function qiblaBearing(lat: number, lng: number): number {
  return Qibla(new Coordinates(lat, lng));
}

/** Next prayer (including sunrise) after `now`, rolling into tomorrow's Fajr. */
export function nextPrayer(s: PrayerSettings, now = new Date()): { key: PrayerKey; at: Date; current: PrayerKey | null } {
  const today = timesFor(s, dayKeyIn(s.tz, now));
  let current: PrayerKey | null = null;
  for (const k of PRAYERS) {
    if (today[k] > now) return { key: k, at: today[k], current };
    current = k;
  }
  const tomorrow = timesFor(s, addDays(today.dayKey, 1));
  return { key: 'fajr', at: tomorrow.fajr, current: 'isha' };
}

export function fmtTime(d: Date, tz: string, h12 = false): string {
  return new Intl.DateTimeFormat(h12 ? 'en-US' : 'en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: h12 }).format(d);
}

/* ── Hijri (Umm al-Qura via Intl — same ICU data in the browser and workerd) ── */
const HIJRI_MONTHS = ['Muharram', 'Safar', 'Rabiʿ al-Awwal', 'Rabiʿ al-Thani', 'Jumada al-Ula', 'Jumada al-Akhirah', 'Rajab', 'Shaʿban', 'Ramadan', 'Shawwal', 'Dhu al-Qaʿdah', 'Dhu al-Hijjah'];
export function hijriOn(dayKey: string, offsetDays = 0): { y: number; m: number; d: number; month: string } | null {
  try {
    const [y, m, d] = dayKey.split('-').map(Number);
    const at = new Date(Date.UTC(y, m - 1, d, 12) + Math.max(-2, Math.min(2, offsetDays)) * 86400000);
    const p = Object.fromEntries(
      new Intl.DateTimeFormat('en-u-ca-islamic-umalqura', { timeZone: 'UTC', year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(at).map((x) => [x.type, x.value]),
    );
    const hy = parseInt(p.year, 10), hm = Number(p.month), hd = Number(p.day);
    if (![hy, hm, hd].every(Number.isFinite)) return null;
    return { y: hy, m: hm, d: hd, month: HIJRI_MONTHS[hm - 1] };
  } catch { return null; }
}

/* ── Notification plan ───────────────────────────────────────────── */

export interface NotifyPrefs {
  adhan: Partial<Record<Exclude<PrayerKey, 'sunrise'>, boolean>>;
  /** minutes before each salah for a heads-up; 0 = off */
  preReminderMin: number;
  sunrise: boolean;
  morningAdhkar: { on: boolean; afterFajrMin: number };
  eveningAdhkar: { on: boolean; afterAsrMin: number };
  dailyGoal: { on: boolean; time: string /* "HH:MM" local */ };
  kahfFriday: boolean;
  lastThird: boolean;
  duha: boolean;
  /** the evening before a sunnah fast day (Mon/Thu, white days, ʿArafah…) */
  fastEve: boolean;
  /** suḥūr (30 min before Fajr) and ifṭār (Maghrib) during Ramadan */
  ramadan: boolean;
  /** the three times voluntary prayer is not prayed (sunrise, zenith, sunset) */
  forbidden?: boolean;
  /** "running late": 60% through a prayer's preferred time, if it isn't logged yet (for each prayer whose adhan is on) */
  late?: boolean;
}

export const DEFAULT_NOTIFY: NotifyPrefs = {
  adhan: { fajr: true, dhuhr: true, asr: true, maghrib: true, isha: true },
  preReminderMin: 0,
  sunrise: false,
  morningAdhkar: { on: true, afterFajrMin: 20 },
  eveningAdhkar: { on: true, afterAsrMin: 30 },
  dailyGoal: { on: true, time: '20:30' },
  kahfFriday: true,
  lastThird: false,
  duha: false,
  fastEve: false,
  ramadan: true,
  forbidden: false,
  late: true,
};

export interface PlannedNotification {
  id: string;          // stable per day + kind: "2026-10-11:adhan-fajr"
  kind: 'adhan' | 'pre' | 'late' | 'adhkar' | 'goal' | 'kahf' | 'night' | 'sunrise' | 'duha' | 'fast' | 'forbidden';
  title: string;
  body: string;
  fireAt: number;      // epoch ms
  url: string;
}

/** "HH:MM on dayKey in tz" → epoch ms (DST-correct double-format technique). */
export function zonedToUtc(dayKey: string, hm: string, tz: string): number {
  const [y, mo, d] = dayKey.split('-').map(Number);
  const [hh, mm] = hm.split(':').map(Number);
  const guess = Date.UTC(y, mo - 1, d, hh, mm, 0);
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
      .formatToParts(new Date(guess)).map((x) => [x.type, x.value]),
  );
  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return guess - (asUTC - guess);
}

/* The three times voluntary prayer is not prayed — "when the sun rises until
   it is fully up, when it stands at its height at midday until it passes the
   meridian, and when it begins to set until it has set" (ʿUqbah ibn ʿĀmir,
   Muslim 831). The minutes are the common estimates: "the height of a spear"
   after sunrise is put at 10–20 min, and the zenith just before Dhuhr. A
   missed obligatory prayer may still be made up at these times. */
export const FORBIDDEN_MIN = { afterSunrise: 15, beforeDhuhr: 10, beforeMaghrib: 15 };

export function forbiddenTimes(t: DayTimes): { key: 'sunrise' | 'zenith' | 'sunset'; label: string; start: Date; end: Date }[] {
  const m = (d: Date, min: number) => new Date(d.getTime() + min * 60_000);
  return [
    { key: 'sunrise', label: 'Sunrise', start: t.sunrise, end: m(t.sunrise, FORBIDDEN_MIN.afterSunrise) },
    { key: 'zenith', label: 'Zenith (before Dhuhr)', start: m(t.dhuhr, -FORBIDDEN_MIN.beforeDhuhr), end: t.dhuhr },
    { key: 'sunset', label: 'Sunset', start: m(t.maghrib, -FORBIDDEN_MIN.beforeMaghrib), end: t.maghrib },
  ];
}

/** `prayed`: the prayers already logged that day (or all five on a day they aren't due) —
    the app sends them, so the "running late" reminder is skipped once you've prayed. */
export function buildDayPlan(s: PrayerSettings, n: NotifyPrefs, dayKey: string, goalMet = false, hijriOffset = 0, prayed: readonly string[] = []): PlannedNotification[] {
  const t = timesFor(s, dayKey);
  const out: PlannedNotification[] = [];
  const at = (d: Date, min = 0) => d.getTime() + min * 60_000;
  const time = (d: Date) => fmtTime(d, s.tz);

  const lateFor = n.late !== false ? SALAH.filter((k) => n.adhan?.[k] && !prayed.includes(k)) : [];
  if (lateFor.length) {
    const w = dayWindows(s, t);
    for (const k of lateFor) {
      const { start, lateAt } = w[k];
      if (lateAt - start < 10 * MIN) continue; // too short a preferred time to warn about halfway through it
      out.push({ id: `${dayKey}:late-${k}`, kind: 'late', title: `Running late for ${PRAYER_LABEL[k].en}?`, body: `You haven’t marked ${PRAYER_LABEL[k].en} as prayed. Its best time ends at ${time(new Date(lateAt))} — there’s still time.`, fireAt: Math.round(start + (lateAt - start) * LATE_REMINDER_AT), url: '/prayer' });
    }
  }

  for (const k of SALAH) {
    if (n.adhan?.[k]) {
      out.push({ id: `${dayKey}:adhan-${k}`, kind: 'adhan', title: `${PRAYER_LABEL[k].en} · ${PRAYER_LABEL[k].ar}`, body: `It's time for ${PRAYER_LABEL[k].en} (${time(t[k])})${s.placeName ? ` in ${s.placeName}` : ''}.`, fireAt: at(t[k]), url: '/prayer' });
    }
    if (n.preReminderMin > 0) {
      out.push({ id: `${dayKey}:pre-${k}`, kind: 'pre', title: `${PRAYER_LABEL[k].en} in ${n.preReminderMin} min`, body: `${PRAYER_LABEL[k].en} begins at ${time(t[k])}.`, fireAt: at(t[k], -n.preReminderMin), url: '/prayer' });
    }
  }
  if (n.sunrise) out.push({ id: `${dayKey}:sunrise`, kind: 'sunrise', title: 'Sunrise', body: `Fajr time has ended (${time(t.sunrise)}).`, fireAt: at(t.sunrise, -15), url: '/prayer' });
  if (n.morningAdhkar?.on) out.push({ id: `${dayKey}:adhkar-morning`, kind: 'adhkar', title: 'Morning adhkar · أذكار الصباح', body: 'Start your day with the remembrance of Allah.', fireAt: at(t.fajr, n.morningAdhkar.afterFajrMin), url: '/adhkar/morning' });
  if (n.eveningAdhkar?.on) out.push({ id: `${dayKey}:adhkar-evening`, kind: 'adhkar', title: 'Evening adhkar · أذكار المساء', body: 'Take a few minutes for your evening adhkar.', fireAt: at(t.asr, n.eveningAdhkar.afterAsrMin), url: '/adhkar/evening' });
  if (n.dailyGoal?.on && !goalMet && /^\d{2}:\d{2}$/.test(n.dailyGoal.time)) out.push({ id: `${dayKey}:goal`, kind: 'goal', title: 'Your daily recitation', body: "You haven't reached today's Quran goal yet — a few minutes is enough.", fireAt: zonedToUtc(dayKey, n.dailyGoal.time, s.tz), url: '/quran?resume=1' });
  const [y, m, d] = dayKey.split('-').map(Number);
  if (n.kahfFriday && new Date(Date.UTC(y, m - 1, d)).getUTCDay() === 5) out.push({ id: `${dayKey}:kahf`, kind: 'kahf', title: 'Jumuʿah Mubarak', body: 'Read Surah al-Kahf today.', fireAt: at(t.fajr, 90), url: '/quran/18' });
  if (n.forbidden) {
    for (const f of forbiddenTimes(t)) {
      out.push({ id: `${dayKey}:forbidden-${f.key}`, kind: 'forbidden', title: `${f.label} — not a time for prayer`, body: `Hold voluntary prayer until ${time(f.end)}. A missed obligatory prayer may still be made up.`, fireAt: f.start.getTime(), url: '/prayer' });
    }
  }
  if (n.duha) out.push({ id: `${dayKey}:duha`, kind: 'duha', title: 'Time for Ḍuḥā · الضحى', body: 'The forenoon prayer — two rakʿahs or more, until shortly before Dhuhr.', fireAt: at(t.sunrise, 30), url: '/prayer' });
  if (n.lastThird) out.push({ id: `${dayKey}:night`, kind: 'night', title: 'Tahajjud · the last third of the night', body: 'A time when duʿāʾ is answered. Rise for Tahajjud.', fireAt: t.lastThird.getTime(), url: '/prayer' });
  // Fasting — rules live in shared/fasting.ts (imported lazily to keep this module's API flat).
  const today = fastingInfo(dayKey, hijriOffset);
  if (n.ramadan && today.ramadan) {
    out.push({ id: `${dayKey}:suhur`, kind: 'fast', title: 'Suḥūr · السحور', body: `Fajr — and the end of suḥūr — is at ${time(t.fajr)}.`, fireAt: at(t.fajr, -30), url: '/fasting' });
    out.push({ id: `${dayKey}:iftar`, kind: 'fast', title: 'Ifṭār · الإفطار', body: 'It’s Maghrib — time to break your fast. اللَّهُمَّ لَكَ صُمْتُ', fireAt: at(t.maghrib), url: '/fasting' });
  }
  const tomorrow = fastingInfo(addDays(dayKey, 1), hijriOffset);
  const reason = tomorrow.rules.find((r) => r.strength === 'sunnah');
  if (n.fastEve && !tomorrow.forbidden && !tomorrow.ramadan && reason) {
    out.push({ id: `${dayKey}:fast-eve`, kind: 'fast', title: `Tomorrow: ${reason.label}`, body: 'A good day to fast — set your intention and an alarm for suḥūr.', fireAt: at(t.isha, 30), url: '/fasting' });
  }
  return out.sort((a, b) => a.fireAt - b.fireAt);
}

/** How late a notification may still be delivered before it's retired. */
export function staleAfterMs(kind: PlannedNotification['kind']): number {
  if (kind === 'adhan' || kind === 'pre' || kind === 'late' || kind === 'sunrise' || kind === 'forbidden') return 10 * 60_000;
  if (kind === 'fast') return 20 * 60_000;
  if (kind === 'kahf') return 3 * 3600_000; // "read al-Kahf today" is still useful hours later
  return 45 * 60_000; // adhkar, goal, ḍuḥā, tahajjud reminders
}
